import React from 'react';

import ChecklistItem from 'src/components/CheckListItem/CheckListItem';
import { calculateProgress, getChecklistItemState } from 'src/util/loading/parallelStageHelpers';

import {
  STAGE_PROGRESS,
  StageKeys,
  TradeListLoadingStates,
} from '../reducer/constants';

/**
 * Checklist items in the order they appear in the checklist.
 * @type {{order: number}[]}
 */
const CHECKLIST_ITEMS = Object.freeze([
  { order: 1, stageKey: StageKeys.PROPERTY },
  { order: 2, stageKey: StageKeys.METERS },
  { order: 3, stageKey: StageKeys.RULES },
  { order: 4, stageKey: StageKeys.TRADES },
  { order: 5, stageKey: StageKeys.DATA_PREPARED },
  { order: 6, stageKey: StageKeys.DATA_RENDERED },
]);

/**
 * Formats the appropriate label for a loading stage based on its completion status.
 * This function determines whether to show a "loading" or "loaded" message for each stage,
 * and includes dynamic count information for meters, rules, and trades when available.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object for internationalization.
 * @param {string} params.stageKey - The stage key identifying which loading stage.
 * @param {string[]} params.activeStages - Array of currently active stage keys.
 * @param {string[]} params.completedStages - Array of completed stage keys.
 * @param {object} params.metadata - The metadata object containing counts (metersCount,
 *   rulesCount, tradesCount) for each stage.
 * @returns {string|React.ReactElement} The label for the specified stage.
 *   Returns a string for simple labels or a React element with formatted counts
 *   and pluralization for stages 2-4.
 */
const formatStageLabel = ({
  intl,
  stageKey,
  activeStages,
  completedStages,
  metadata,
}) => {
  const normalizeCount = (count) => {
    if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) {
      return 0;
    }
    return count;
  };

  const metersCount = normalizeCount(metadata?.metersCount);
  const rulesCount = normalizeCount(metadata?.rulesCount);
  const tradesCount = normalizeCount(metadata?.tradesCount);
  const isCompleted = completedStages.includes(stageKey);
  const isActive = activeStages.includes(stageKey);

  switch (stageKey) {
    case StageKeys.PROPERTY: {
      const propertyLoadedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.property_loaded.label',
        defaultMessage: 'Property loaded',
      });
      const loadingPropertyProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_property.label',
        defaultMessage: 'Loading property',
      });
      return isCompleted ? propertyLoadedProgressBarLabel : loadingPropertyProgressBarLabel;
    }

    case StageKeys.METERS: {
      const isLoadingMetersWithCount = isActive && metersCount > 0;

      if (isCompleted || isLoadingMetersWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.meters_loaded.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> meter} other {<strong>#</strong> meters}} loaded',
          },
          { count: metersCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }

      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_meters.label',
        defaultMessage: 'Loading meters',
      });
    }

    case StageKeys.RULES: {
      const isLoadingRulesWithCount = isActive && rulesCount > 0;

      if (isCompleted || isLoadingRulesWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.trade_rules_loading.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade rule} other {<strong>#</strong> trade rules}} loaded',
          },
          { count: rulesCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }
      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_rules.label',
        defaultMessage: 'Loading rules',
      });
    }

    case StageKeys.TRADES: {
      const isLoadingTradesWithCount = isActive && tradesCount > 0;

      if (isCompleted || isLoadingTradesWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.trades_loading.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade} other {<strong>#</strong> trades}} loaded',
          },
          { count: tradesCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }
      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_trades.label',
        defaultMessage: 'Loading trades',
      });
    }

    case StageKeys.DATA_PREPARED: {
      const dataPreparedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.data_prepared.label',
        defaultMessage: 'Data prepared',
      });
      const preparingDataProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.preparing_data.label',
        defaultMessage: 'Preparing data',
      });
      return isCompleted ? dataPreparedProgressBarLabel : preparingDataProgressBarLabel;
    }

    case StageKeys.DATA_RENDERED: {
      const dataRenderedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.data_rendered.label',
        defaultMessage: 'Data rendered',
      });
      const renderingDataProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.rendering_data.label',
        defaultMessage: 'Rendering data',
      });
      return isCompleted ? dataRenderedProgressBarLabel : renderingDataProgressBarLabel;
    }

    default:
      return '';
  }
};

/**
 * Returns a checklist-style progress label showing all loading stages with dynamic messages.
 * Supports three visual states: completed (green), active (yellow), and pending (grey).
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object.
 * @param {string[]} params.activeStages - Array of currently active stage keys.
 * @param {string[]} params.completedStages - Array of completed stage keys.
 * @param {object} params.metadata - The metadata object containing counts for each stage.
 * @returns {React.ReactElement} A JSX element containing the checklist.
 */
const displayChecklistProgress = ({
  intl,
  activeStages,
  completedStages,
  metadata,
}) => (
  <div>
    {CHECKLIST_ITEMS.map((item) => {
      const state = getChecklistItemState(item.stageKey, activeStages, completedStages);
      const label = formatStageLabel({
        intl,
        stageKey: item.stageKey,
        activeStages,
        completedStages,
        metadata,
      });
      return (
        <ChecklistItem key={item.stageKey} state={state} label={label} />
      );
    })}
  </div>
);

/**
 * Generates loading overlay configuration based on the current loading state.
 * This function maps the TradeList loading state machine's current state to
 * the appropriate overlay configuration, providing user feedback during the
 * multi-stage trade data fetching pipeline. Each loading stage displays
 * a checklist showing all stages with checkboxes marked as completed.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object for internationalization.
 * Used to format all overlay text messages with proper locale support.
 * @param {object} params.date - A Luxon DateTime object representing the date
 * being loaded. Displayed in the overlay body to provide context to the user.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @param {Function} [params.onCancelFunc] - Optional cancel callback for the overlay button.
 * @returns {{ loadingOverlayProps: object|null, overlayVisible: boolean }} Overlay configuration
 * object with `loadingOverlayProps` and `overlayVisible` properties. See return value
 * structure documentation above.
 */
const buildLoadingOverlayState = ({
  intl,
  date,
  loadingState,
  onCancelFunc,
}) => {
  if (!loadingState || !intl || !date) {
    return { loadingOverlayProps: null, overlayVisible: false };
  }
  const {
    state,
    metadata,
    activeStages = [],
    completedStages = [],
    overlay,
  } = loadingState || {};

  const headerLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.build_loading_overlay_state.overlay.header.label',
    defaultMessage: 'Loading Trade Data',
  });
  const bodyLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.build_loading_overlay_state.overlay.body.label',
    defaultMessage: 'Loading trade data for {date}:',
  }, { date: date.toISODate() });

  const progressLabel = displayChecklistProgress({
    intl,
    activeStages,
    completedStages,
    metadata,
  });

  const progressBarValue = overlay?.progressBarValue
  ?? calculateProgress(completedStages, STAGE_PROGRESS);
  const cancelLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.build_loading_overlay_state.overlay.cancel.label',
    defaultMessage: 'Cancel',
  });

  switch (state) {
    case TradeListLoadingStates.IDLE:
    case TradeListLoadingStates.ERROR:
    case TradeListLoadingStates.CANCELLED:
      return { loadingOverlayProps: null, overlayVisible: false };
    case TradeListLoadingStates.INITIALISING:
    case TradeListLoadingStates.LOADING_METERS:
    case TradeListLoadingStates.LOADING_RULES:
    case TradeListLoadingStates.LOADING_TRADES:
    case TradeListLoadingStates.PREPARING_DATA:
    case TradeListLoadingStates.RENDERING_DATA:
    case TradeListLoadingStates.COMPLETE:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel,
          updateProgress: () => {},
          progressBarValue,
          onCancelFunc,
          cancelLabel,
        },
        overlayVisible: true,
      };
    default:
      return { loadingOverlayProps: null, overlayVisible: false };
  }
};

export default buildLoadingOverlayState;
